import { faArrowRightLong, faCircleXmark, faEquals, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Button, Col, Collapse, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Tooltip } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DatabaseEntityContact, EntityUserAuthorization, getUserEntityAuthorizations, saveEntityAuthorizationsByUser } from '../../redux/ContactSlice';
import { EntityAuthorization } from '../../redux/EntitySlice';
import { isAdmin } from '../../redux/MiscSlice';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import utilityHelper from '../../utils/utilityHelper';
import { downloadDocumentToBrowser } from '../../redux/DocumentsSlice';
import { useDebouncedCallback } from 'use-debounce';



interface UserAuthorizationListItem extends DatabaseEntityContact {
    entityName: string;
    enabled: boolean;
    checked: boolean;
}

interface entitySyncerProps {
    userEmailValue: string; 
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    contactToSync: DatabaseEntityContact | undefined;
    entityCode: string;
}

const EntityAuthorizationSyncer = (props: entitySyncerProps) => {

    const dispatch = useAppDispatch();

    const isAdminUser: boolean = useAppSelector(isAdmin);

    const [entityAuthOptions, setEntityAuthOptions] = React.useState<Array<EntityUserAuthorization>>([]);
    const [userAuthorizationListItems, setUserAuthorizationListItems] = React.useState<Array<UserAuthorizationListItem>>([]);
    const [userLogin, setUserLogin] = React.useState<string>("");
    const [hasItemChecked, setHasItemChecked] = React.useState<boolean>(false);
    const [isLoadingList, setIsLoadingList] = React.useState<boolean>(false);
    const [selectAllIsChecked, setSelectAllIsChecked] = React.useState<boolean>(false);
    const [someOptionsEnabled, setSomeOptionsEnabled] = React.useState<boolean>(false);
    const [userEmail, setUserEmail] = React.useState<string>("");
    const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);


    const toggleModal = () => {
        setModalIsOpen(!props.isOpen);
        props.setIsOpen(!props.isOpen);
    }

    const handleAuthorizationChanged = (checked: boolean, listItem: UserAuthorizationListItem) => {
        const updatedItems = userAuthorizationListItems.map(item => item.entityCode === listItem.entityCode
            && item.email == listItem.email
           ? { ...item, checked: checked } : item);
        setUserAuthorizationListItems(updatedItems);
        let atLeastOneChecked = updatedItems.some((item) => item.checked == true);

        if (hasItemChecked != atLeastOneChecked) {
            setHasItemChecked(atLeastOneChecked);
        }
    }

    const handleSaveChanges = () => {

        let changedAuthorizations = userAuthorizationListItems.filter((item) => item.checked == true);

        const databaseEntityContacts = changedAuthorizations.map(item => item.entityCode === item.entityCode ? { ...item, entityAuthorizations: props.contactToSync!.entityAuthorizations} : item);
       
        dispatch(saveEntityAuthorizationsByUser(databaseEntityContacts)).then((result: any) => {

            const doc = unwrapResult(result);
            if (doc !== null) {
                downloadDocumentToBrowser(doc);
            }

            props.setIsOpen(false);
            clearHooks();
        });
    }

    const handleSelectAllCheckedChanged = (checked: boolean) => {

        if (checked != selectAllIsChecked) {
            setSelectAllIsChecked(checked);
            const updatedItems = userAuthorizationListItems.map(item => item.enabled == true ? {
                ...item,
                checked: checked
            } : item);

            setUserAuthorizationListItems(updatedItems);


            let atLeastOneChecked = updatedItems.some((item) => item.checked == true);

            if (hasItemChecked != atLeastOneChecked) {
                setHasItemChecked(atLeastOneChecked);
            }
        }
    }
  
    const clearHooks = () => {

        setUserLogin("");
        setEntityAuthOptions([]);
        setUserAuthorizationListItems([]);
        setUserEmail("");
        setSelectAllIsChecked(false);
    }

    const [detailPanelIsOpen, setDetailPanelIsOpen] = React.useState<boolean>(false);

    const getChangesRenderGivenAuthorization = (): JSX.Element => {

        let elements: JSX.Element[] = [];

        if (selectedUserListItem != null) {
            var relatingOptionValues = entityAuthOptions.filter((auth: EntityUserAuthorization) => auth.entity.code == selectedUserListItem.entityCode);

            if (relatingOptionValues) {

                let workingElements: JSX.Element[] = [];

                var firstOptionValue = relatingOptionValues.find((auth: EntityUserAuthorization) => auth.entity.code == selectedUserListItem.entityCode);

                workingElements.push(
                    
                        <Col>
                            <Row>
                                <Col>
                                    {firstOptionValue ? firstOptionValue.user.firstName : null}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {firstOptionValue ? firstOptionValue.user.lastName : null}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {firstOptionValue ? firstOptionValue.title : null}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {firstOptionValue ? firstOptionValue.phoneNumber : null}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {firstOptionValue ? firstOptionValue.extension : null}
                                </Col>
                            </Row>
                        </Col>
                                       
                )

                for (var i = 0; i < relatingOptionValues.length; i++) {
                    workingElements.push(
                        
                        <Col>
                            <Row>
                                <Col>
                                    {utilityHelper.getReportTitleValue(relatingOptionValues[i].reportAccess)}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {utilityHelper.SnakeCaseToTitleCase(relatingOptionValues[i].entityUserRole)}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    {utilityHelper.SnakeCaseToTitleCase(relatingOptionValues[i].entityContactRole)}
                                </Col>
                            </Row>
                        </Col>
                                                
                    )
                }

                elements.push(<Row>
                    {workingElements}
                </Row>                    
               )
            } 
        }
       
        return (<React.Fragment>
            {elements}
        </React.Fragment>);
    }

    React.useEffect(() => {

        if (props.isOpen == true) {
            if (isAdminUser == true) {
                setIsLoadingList(true);
                if (props.userEmailValue === "") {
                    setUserLogin("");
                    setEntityAuthOptions([]);
                    setUserAuthorizationListItems([]);
                } else {
                    setUserLogin(props.userEmailValue);

                    dispatch(getUserEntityAuthorizations(props.userEmailValue)).then((result: any) => {
                        var originalPromiseResult: Array<EntityUserAuthorization> | null = unwrapResult(result);

                        if (originalPromiseResult) {
                            setEntityAuthOptions(originalPromiseResult);
                        } else {
                            setEntityAuthOptions([]);
                        }
                    });
                }
                setIsLoadingList(false);
            }
        } else {
            clearHooks();
        }      
    }, [props.isOpen]); 

    React.useEffect(() => {

        if (props.userEmailValue.length > 0) {
            if (props.userEmailValue != userEmail) {
                setUserEmail(props.userEmailValue);
            }
        }

    }, [props.userEmailValue])

    React.useEffect(() => {

        if (props.contactToSync !== undefined) {

            let userAuthListItems: Array<UserAuthorizationListItem> = [];

            var filteredEntityOptions = entityAuthOptions.filter((options) => options.entity.code != props.entityCode);

            var afrOption = props.contactToSync.entityAuthorizations.find((auth: EntityAuthorization) => auth.reportAccess == TaxonomyTypeEnum.AFR);
            var cr939Option = props.contactToSync.entityAuthorizations.find((auth: EntityAuthorization) => auth.reportAccess == TaxonomyTypeEnum.COURT_REPORTING_939);
            var cr29Option = props.contactToSync.entityAuthorizations.find((auth: EntityAuthorization) => auth.reportAccess == TaxonomyTypeEnum.COURT_REPORTING_29);

            // Group EntityUserAuthorization by entityCode
            const groupedEntUseAuth = new Map<string, UserAuthorizationListItem>();

            for (const auth of filteredEntityOptions) {
                const code = auth.entity.code;
                let enabledValue = true;
                //if (auth.reportAccess == TaxonomyTypeEnum.AFR && afrOption) {
                //    if (afrOption.entityContactRole != auth.entityContactRole
                //        || afrOption.entityUserRole != auth.entityUserRole) {
                //        enabledValue = true;
                //    }
                //} else if (auth.reportAccess == TaxonomyTypeEnum.COURT_REPORTING_939 && cr939Option) {
                //    if (cr939Option.entityContactRole != auth.entityContactRole
                //        || cr939Option.entityUserRole != auth.entityUserRole) {
                //        enabledValue = true;
                //    }
                //} else if (auth.reportAccess == TaxonomyTypeEnum.COURT_REPORTING_29 && cr29Option) {
                //    if (cr29Option.entityContactRole != auth.entityContactRole
                //        || cr29Option.entityUserRole != auth.entityUserRole) {
                //        enabledValue = true;
                //    }
                //} else if (auth.title != props.contactToSync.title) {
                //    enabledValue = true;
                //} else if (auth.user.firstName != props.contactToSync.firstName) {
                //    enabledValue = true;
                //} else if (auth.user.lastName != props.contactToSync.lastName) {
                //    enabledValue = true;
                //} else if (auth.phoneNumber != props.contactToSync.phoneNumber) {
                //    enabledValue = true;
                //} else if (auth.extension != props.contactToSync.extension) {
                //    enabledValue = true;
                //}

                if (!groupedEntUseAuth.has(code)) {
                    groupedEntUseAuth.set(code, {
                        entityCode: auth.entity.code,
                        entityName: auth.entity.name,
                        checked: false,
                        enabled: enabledValue,
                        entityContactId: null,
                        title: props.contactToSync.title,                  
                        startDate: null,
                        endDate: null,
                        logerUserId: null,
                        userName: null,
                        firstName: props.contactToSync.firstName,
                        lastName: props.contactToSync.lastName,
                        contactInfoId: null,
                        email: auth.user.login,
                        phoneNumber: props.contactToSync.phoneNumber,
                        extension: props.contactToSync.extension,
                        addressId: null,
                        cityCode: null,
                        street: null,
                        pobox: null,
                        zip: null,
                        entityAuthorizations: []
                    });
                };
                groupedEntUseAuth.get(code)!.entityAuthorizations.push(auth as EntityAuthorization);
            }

            const items = Array.from(groupedEntUseAuth.values());
            userAuthListItems.push(...items);         

            if (userAuthListItems.some((item: UserAuthorizationListItem) => item.enabled == true)) {
                //sort by entity name alphabetically
                setUserAuthorizationListItems(userAuthListItems.sort((a: UserAuthorizationListItem, b: UserAuthorizationListItem) => {
                    const nameA = a.entityName.toLowerCase();
                    const nameB = b.entityName.toLowerCase();
                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                }));
                setSomeOptionsEnabled(true);
                setModalIsOpen(true);
            } else {
                setModalIsOpen(false);
                setSomeOptionsEnabled(false);
                props.setIsOpen(false);
            }
        }
        

        
    }, [entityAuthOptions])

    const [selectedUserListItem, setSelectedUserListItem] = React.useState<UserAuthorizationListItem | null>(null);
    const togglePreviousModal = () => setDetailPanelIsOpen(!detailPanelIsOpen);

    const closeSuggestionDebouce = useDebouncedCallback(() => {
        setDetailPanelIsOpen(false);
    }, 5000);

    const handleDetailRowClick = (item: UserAuthorizationListItem) => {
        setSelectedUserListItem(item);
        closeSuggestionDebouce();
        setDetailPanelIsOpen(true);
    }

    const isAuthorizedRender = (
        <React.Fragment>

            <Modal isOpen={modalIsOpen} toggle={toggleModal} className="authorizationModal">
                <ModalHeader
                    toggle={toggleModal}
                    style={{ border: "none" }}
                ></ModalHeader>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10" style={{ textAlign: "center" }}>
                        <h2> User Associated With Multiple Entities!</h2>
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10" style={{ textAlign: "center" }}>
                        <FontAwesomeIcon className="menuIcon" icon={faTriangleExclamation} style={{ color: "#ea0b0b", }} />
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>
                    <Col md="1"></Col>
                    <Col md="10">
                        <p>Select the checkbox next to each entity if you want the same change made to that account. <a href="#">{userLogin}</a> is associated with the following entities:</p>
                    </Col>
                    <Col md="1"></Col>
                </Row>
                <Row>              
                    <Col className="ml-4">
                        <Label checked>
                            <Input
                                type="checkbox"
                                checked={selectAllIsChecked}
                                disabled={someOptionsEnabled == false }
                                onChange={(e) => handleSelectAllCheckedChanged(e.target.checked)} /> {' Select All'}
                        </Label>
                    </Col>  
                </Row>
                <ModalBody style={{ overflowY: "scroll" }}>
                    <Row>
                        <Col>
                        </Col>
                    </Row>
                    <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                        type="grow" color="warning"
                        hidden={isLoadingList == false} />

                    <div hidden={isLoadingList == true}>

                        <Row>
                            <Col>
                                {userAuthorizationListItems.map((item, index) => {
                                    
                                    return (
                                        <Row key={index} style={{
                                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#e9ecef',
                                        }}
                                            hidden={item.enabled == false}
                                            id={"row-" + index}
                                            
                                        >
                                            <Col md="12">
                                                <Row>
                                                    <Col>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md="1">
                                                        <Row>
                                                            <Col>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <Label className="ml-4" checked>
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={item.checked}
                                                                        disabled={item.enabled == false}
                                                                        onChange={(e) => handleAuthorizationChanged(e.target.checked, item)} />
                                                                </Label>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                            </Col>
                                                        </Row>   
                                                    </Col>
                                                    <Col md="11">
                                                        <Label className="ml-1"
                                                            onClick={() => { handleDetailRowClick(item) }}
                                                        >
                                                            {' ' + item.entityName}
                                                        </Label>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                    </Col>
                                                </Row>                                      
                                            </Col>
                                        </Row>
                                    );
                                })}
                            </Col>
                            <Modal isOpen={detailPanelIsOpen == true} toggle={togglePreviousModal}>
                                <ModalHeader
                                    toggle={togglePreviousModal}
                                    style={{ border: "none" }}
                                ></ModalHeader>
                                <ModalBody>
                                    <Row>
                                        <Col>
                                            <Label className="ml-1">Previous Values</Label>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            {getChangesRenderGivenAuthorization()}
                                        </Col>
                                    </Row>
                                </ModalBody>
                            </Modal>
                        </Row>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Row>
                        <Col md="5" style={{ textAlign: "left" }}>
                            <Button color="danger" onClick={() => { toggleModal(); setUserAuthorizationListItems([]); }}>Do not make changes to any other entities</Button>
                        </Col>
                        <Col md="5" style={{ textAlign: "right" }}>
                            <Button className="taxedit" disabled={hasItemChecked == false} onClick={() => { handleSaveChanges() } }>Make changes to all selected entities</Button>
                        </Col>
                    </Row>

                </ModalFooter>
            </Modal>
        </React.Fragment>
    );

    const isNotAuthorizedRender = (
        <></>
    );

    return (
        <React.Fragment>
            {isAdminUser == true && userLogin && userLogin != "" ? isAuthorizedRender : isNotAuthorizedRender}
        </React.Fragment>
    );

}


export default EntityAuthorizationSyncer;






